'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import {
  lu,
  MarketPrompt,
  au,
  cu,
  CampaignAsset,
  $l,
  ou,
  su,
  MarketImage,
} from './market-primitives';
import { U, MarketReveal, Yl } from './collection-animation';
import { ShowcaseIcon } from './scene-primitives';
export function CampaignKit({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = lu(e);
  return (
    <div className={'flex flex-col gap-4'}>
      <MarketPrompt
        t={e}
        workflow={'Create a Campaign Kit'}
        brand={'aubrac'}
        prompt={`launch for ${U.ref}: teaser, launch and last call`}
        doneAt={au.done}
        busy={'Making the campaign kit'}
        result={'Campaign kit ready'}
        meta={'3 moments \xB7 3 formats'}
      />
      <div className={'grid gap-3 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-4'}>
        <div className={'flex min-w-0 flex-col gap-3'}>
          <div className={'flex items-end gap-2 sm:gap-3'}>
            {translateShowcaseNode(
              showcaseCopy,
              cu.map((n, r) => (
                <MarketReveal
                  key={n.key}
                  pop={!0}
                  on={e >= au.frames + r * 140}
                  as={'figure'}
                  className={'m-0 flex flex-col gap-1.5'}
                >
                  <span
                    className={`block h-[118px] sm:h-[200px] lg:h-[264px] ${n.frame}`}
                  >
                    <CampaignAsset format={n} moment={t} t={e} />
                  </span>
                  <figcaption
                    className={'text-ink-3 px-0.5 text-[12px] leading-4'}
                  >
                    {translateShowcaseNode(showcaseCopy, n.label)}
                  </figcaption>
                </MarketReveal>
              ))
            )}
          </div>
          <ol className={'grid grid-cols-3 gap-2'}>
            {translateShowcaseNode(
              showcaseCopy,
              Yl.map((n, r) => {
                let i = r === t,
                  a = $l(e, ou[r] ?? 0, ou[r + 1] ?? su);
                return (
                  <MarketReveal
                    key={n.key}
                    as={'li'}
                    on={e >= au.strip + r * 160}
                    className={`flex min-w-0 flex-col gap-2 rounded-[10px] p-1.5 transition-[background-color] duration-200 ${i ? 'bg-[var(--state-wash)]' : ''}`}
                  >
                    <span className={'flex min-w-0 items-center gap-2'}>
                      <MarketImage
                        asset={n.asset}
                        className={'h-10 w-8 shrink-0 rounded-[4px]'}
                      />
                      <span className={'min-w-0'}>
                        <span
                          className={
                            'text-ink block text-[13px] leading-5 font-medium'
                          }
                        >
                          {translateShowcaseNode(showcaseCopy, n.label)}
                        </span>
                        <span
                          className={
                            'text-ink-3 block truncate text-[12px] leading-4 max-sm:hidden'
                          }
                        >
                          {translateShowcaseNode(showcaseCopy, n.shot)}
                        </span>
                      </span>
                    </span>
                    <span
                      className={
                        'h-0.5 overflow-hidden rounded-full bg-[var(--state-wash)]'
                      }
                    >
                      <span
                        className={
                          'block h-full origin-left bg-[var(--primary)]'
                        }
                        style={{
                          transform: `scaleX(${a})`,
                        }}
                      />
                    </span>
                  </MarketReveal>
                );
              })
            )}
          </ol>
        </div>
        <MarketReveal
          on={e >= au.copy}
          className={'mk-card flex flex-col gap-3 p-3 sm:p-4'}
        >
          <div>
            <span className={'label'}>
              {translateShowcaseNode(showcaseCopy, 'Idea')}
            </span>
            <p className={'mk-serif text-ink mt-0.5 text-[26px] leading-8'}>
              {translateShowcaseNode(showcaseCopy, U.idea)}
            </p>
            <p className={'text-ink-3 mt-1 text-[13px] leading-5 text-pretty'}>
              {translateShowcaseNode(showcaseCopy, U.line)}
            </p>
          </div>
          <div className={'h-px bg-[var(--line)]'} />
          <div>
            <span className={'label'}>
              {translateShowcaseNode(showcaseCopy, 'Headlines')}
            </span>
            <ol className={'mt-1.5 flex flex-col gap-0.5'}>
              {translateShowcaseNode(
                showcaseCopy,
                Yl.map((n, r) => (
                  <MarketReveal
                    key={n.key}
                    as={'li'}
                    on={e >= au.rows + r * 150}
                    className={`flex items-baseline gap-2.5 rounded-[8px] px-2 py-1.5 text-[13px] leading-5 transition-[background-color,color] duration-200 ${r === t ? 'text-ink bg-[var(--state-wash)]' : 'text-ink-3'}`}
                  >
                    <span className={'text-ink-4 w-16 shrink-0 text-[12px]'}>
                      {translateShowcaseNode(showcaseCopy, n.label)}
                    </span>
                    {translateShowcaseNode(showcaseCopy, n.headline)}
                  </MarketReveal>
                ))
              )}
            </ol>
          </div>
          <MarketReveal on={e >= au.chips} className={'flex flex-wrap gap-1.5'}>
            {translateShowcaseNode(
              showcaseCopy,
              U.formats.map((e) => (
                <span
                  key={e}
                  className={
                    'text-ink-3 rounded-full bg-[var(--state-wash)] px-2.5 py-0.5 text-[12px] leading-5'
                  }
                >
                  {translateShowcaseNode(showcaseCopy, e)}
                </span>
              ))
            )}
          </MarketReveal>
          <MarketReveal
            on={e >= au.checks}
            className={
              'text-ink-3 flex gap-2 border-t border-[var(--line)] pt-3 text-[12px] leading-[18px]'
            }
          >
            <ShowcaseIcon
              name={'clipboard'}
              className={'mt-px size-4 shrink-0'}
            />
            {translateShowcaseNode(showcaseCopy, U.checks)}
          </MarketReveal>
        </MarketReveal>
      </div>
    </div>
  );
}
export const fu = [
  'Feb 9',
  'Feb 16',
  'Feb 23',
  'Mar 2',
  'Mar 9',
  'Mar 16',
  'Mar 23',
];
export const pu = 4 + 3 / 7;
export const mu = [
  {
    name: 'Campaign kit',
    owner: 'Brand',
    from: 0,
    to: 2.2,
  },
  {
    name: 'Listing images',
    owner: 'E-commerce',
    from: 1,
    to: 2.6,
  },
  {
    name: 'Product video',
    owner: 'Brand',
    from: 1.2,
    to: 3.6,
  },
  {
    name: 'Product page',
    owner: 'E-commerce',
    from: 2,
    to: pu,
  },
  {
    name: 'Launch email',
    owner: 'CRM',
    from: 3.4,
    to: pu,
  },
  {
    name: 'Social posts',
    owner: 'Social',
    from: 3.8,
    to: 6,
  },
  {
    name: 'Read market response',
    owner: 'Merchandising',
    from: 5,
    to: 7,
  },
];
export const hu = {
  card: 300,
  grid: 520,
  lane: 760,
  laneStep: 240,
  done: 3300,
};
export const gu = hu.lane + mu.length * hu.laneStep;
export const _u = (e) => `${(e / fu.length) * 100}%`;
